// İSG uzmanı portalı — İzne personel ekleme (Modal + çoklu seçim). api-contract §14.3
// (`POST /permits/{uuid}/personnel {personnelUuids}`). Seçenekler iznin **taşeronuna** bağlı
// personelden (§13.3 `GET /contractors/{uuid}/personnel`) doldurulur; adaylar `isPermitCandidate`:
// `Active` ya da `NoLogin`, her zaman `isActive === true` — pasif kişi asla listelenmez; az önce
// oluşturulan kişi liste yenilemesi gelmeden listeye katılır. Zaten kapsanan personel çıkarılır.
// İdempotent (BE zaten kapsanan personeli atlar). Personel iznin taşeronuna + firmaya atanmış
// değilse → `404 PERSONNEL_NOT_FOUND` (kısmi yazma yok).

import {
  Alert,
  Button,
  EmptyState,
  Modal,
  PersonnelCreateSheet,
  PersonnelMaskNotice,
  Spinner,
  Tag,
  contractorsApi,
  isNotActivated,
  permitsApi,
  resolveApiErrorMessage,
  usePermitCandidateOptions,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { type RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permitsKeys } from '../../lib/permitsKeys';
import { usePersonnelActivationClient } from '../../lib/personnelActivationClient';

const NOT_ACTIVATED_HINT_ID = 'permit-personnel-not-activated-hint';

interface PermitPersonnelAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  contractorUuid: string;
  /** Zaten kapsanan personel public kimlikleri — seçenekten çıkarılır. */
  coveredUuids: string[];
  /** `detail.canCreatePersonnel === true` (GL6 §4.8): "Yeni personel oluştur" yalnız oluşturabilene. */
  canCreate: boolean;
  /** Panelin "Personel ekle" düğmesi: modal kapanınca odak buraya döner (R1). */
  restoreFocusRef: RefObject<HTMLElement | null>;
}

export function PermitPersonnelAddModal({
  open,
  onOpenChange,
  permitUuid,
  contractorUuid,
  coveredUuids,
  canCreate,
  restoreFocusRef,
}: PermitPersonnelAddModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activation = usePersonnelActivationClient(contractorUuid);
  const [creating, setCreating] = useState(false);

  const personnelQuery = useQuery({
    queryKey: permitsKeys.personnelOptions(contractorUuid),
    queryFn: () => contractorsApi.listContractorPersonnel(contractorUuid),
    enabled: open && contractorUuid.length > 0,
    staleTime: 60_000,
  });

  const {
    options,
    selected,
    visibleSelected,
    toggle,
    checkboxRef,
    createTriggerRef,
    sheetRestoreRef,
    handleCreated,
    reset,
  } = usePermitCandidateOptions(personnelQuery.data, coveredUuids);

  const mutation = useMutation({
    mutationFn: () =>
      permitsApi.addPermitPersonnel(permitUuid, { personnelUuids: visibleSelected }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
      resetLocal();
      onOpenChange(false);
    },
  });

  function resetLocal() {
    reset();
    setCreating(false);
  }
  // Modalın kendi kapanışı ve "Vazgeç" aynı yoldan geçer: yerel durum + hata sıfırlanır.
  function close() {
    mutation.reset();
    resetLocal();
    onOpenChange(false);
  }

  const noOptions = personnelQuery.isSuccess && options.length === 0;
  const createButton = canCreate ? (
    <Button
      ref={createTriggerRef}
      variant={noOptions ? 'secondary' : 'ghost'}
      size="sm"
      onClick={() => setCreating(true)}
      disabled={mutation.isPending}
    >
      <UserPlus className="h-4 w-4" aria-hidden="true" />
      {t('permits.personnel.add.createNew')}
    </Button>
  ) : null;

  return (
    <>
      <Modal
        open={open && !creating}
        onOpenChange={(next) => {
          if (next) {
            onOpenChange(true);
          } else {
            close();
          }
        }}
        restoreFocusRef={restoreFocusRef}
        title={t('permits.personnel.add.title')}
        description={t('permits.personnel.add.description')}
        size="md"
        closeLabel={t('permits.common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
              {t('permits.common.cancel')}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={visibleSelected.length === 0}
            >
              {mutation.isPending ? (
                <Spinner label={t('permits.common.add')} />
              ) : (
                t('permits.common.add')
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}
          {options.some((p) => p.personalDataMasked) ? <PersonnelMaskNotice /> : null}

          {personnelQuery.isPending ? (
            <Spinner label={t('permits.common.loading')} />
          ) : personnelQuery.isError ? (
            // R2: hata dalı yoktu — başarısız sorgu boş kenarlıklı bir kutu çiziyordu.
            <Alert tone="danger">{resolveApiErrorMessage(personnelQuery.error, t)}</Alert>
          ) : noOptions ? (
            // Boş durumda da oluşturma yolu var (yalnız oluşturabilene).
            <EmptyState
              variant="panel"
              title={t('permits.personnel.add.empty')}
              action={createButton}
            />
          ) : (
            <>
              <div className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
                {options.map((p) => {
                  const name = `${p.firstName} ${p.lastName}`.trim();
                  return (
                    <label
                      key={p.uuid}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-fg hover:bg-surface-muted"
                    >
                      <input
                        ref={checkboxRef(p.uuid)}
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-primary"
                        checked={selected.has(p.uuid)}
                        onChange={() => toggle(p.uuid)}
                        aria-describedby={isNotActivated(p) ? NOT_ACTIVATED_HINT_ID : undefined}
                      />
                      {/* R2: kullanıcı verisi — uzun ad/e-posta satırı taşırmasın. */}
                      <span
                        className="min-w-0 max-w-[55%] shrink-0 truncate font-medium"
                        title={name}
                      >
                        {name}
                      </span>
                      {p.emailOrPhone ? (
                        <span className="min-w-0 truncate text-muted" title={p.emailOrPhone}>
                          · {p.emailOrPhone}
                        </span>
                      ) : null}
                      {isNotActivated(p) ? (
                        <span className="shrink-0">
                          <Tag color="gray">{t('permits.personnel.notActivated')}</Tag>
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              {options.some((p) => isNotActivated(p)) ? (
                <p id={NOT_ACTIVATED_HINT_ID} className="text-xs text-muted">
                  {t('permits.personnel.notActivated.hint')}
                </p>
              ) : null}
              {createButton ? <div>{createButton}</div> : null}
            </>
          )}
        </div>
      </Modal>

      {canCreate ? (
        <PersonnelCreateSheet
          open={creating}
          onOpenChange={setCreating}
          client={activation}
          onCreated={handleCreated}
          restoreFocusRef={sheetRestoreRef}
        />
      ) : null}
    </>
  );
}
